import type { App } from 'vue';
import type { FormKitNode } from '@formkit/core';
import { undefine, whenAvailable } from '@formkit/utils';
import { plugin, defaultConfig, createInput } from '@formkit/vue';
// eslint-disable-next-line no-restricted-imports
import { FormKitMessages } from '@formkit/vue';
import { createProPlugin, inputs } from '@formkit-enterprise/pro';
import { en, nl } from '@formkit/i18n';

import FormulateAmount from '@/formkit/AmountInput.vue';
import FindAndSelect from '@/formkit/FindAndSelectInput.vue';
import Quantity from '@/formkit/QuanitytInput.vue';
import { weekDatepicker } from '@/formkit/weekDatepicker';
import FormulateInlineSelect from '@/formkit/InlineSelectInput.vue';
import PhoneNumberInput from '@/formkit/PhoneNumberInput.vue';
import { getCurrencySymbol } from '@/helpers/currencyHelpers';
import {
  errorHandler,
  getPhoneNumberValidationMessage,
  postalCodeValidation,
  getPostcodeValidationMessage,
  phoneNumberValidation,
  naturallyBefore,
  naturallyAfter,
  getNaturalOrderValidationMessage,
  taxIdFormatValidation,
  getTaxIdFormatValidationMessage,
} from '@/helpers/formkit';

export function initFormKit(app: App) {
  const pro = createProPlugin('fk-5bc5379d24c', { ...inputs, weekDatepicker });
  app.component('FormKitMessages', FormKitMessages);

  app.use(
    plugin,
    defaultConfig({
      icons: {
        checkboxDecorator:
          '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8.34356 20L1 12.4158L2.83589 10.5198L8.34356 16.2079L20.1641 4L22 5.89605L8.34356 20Z"/></svg>',
        locationPin:
          '<svg viewBox="0 0 16 16" fill="currentColor"><path d="M8,16c-.16,0-.31-.07-.41-.19-.3-.35-2.93-3.45-4.25-6.25-.6-1.27-.84-2.31-.84-3.56,0-3.03,2.47-5.5,5.5-5.5s5.5,2.47,5.5,5.5c0,1.25-.24,2.29-.84,3.56-1.32,2.8-3.95,5.9-4.25,6.25-.1,.12-.25,.19-.41,.19Zm0-14.5c-2.48,0-4.5,2.02-4.5,4.5,0,1.1,.21,1.98,.75,3.13,1.05,2.22,3.02,4.72,3.75,5.62,.73-.9,2.7-3.4,3.75-5.62,.54-1.15,.75-2.03,.75-3.13,0-2.48-2.02-4.5-4.5-4.5Z"/><path d="M8,8.5c-1.38,0-2.5-1.12-2.5-2.5s1.12-2.5,2.5-2.5,2.5,1.12,2.5,2.5-1.12,2.5-2.5,2.5Zm0-4c-.83,0-1.5,.67-1.5,1.5s.67,1.5,1.5,1.5,1.5-.67,1.5-1.5-.67-1.5-1.5-1.5Z"/></svg>',
      },
      plugins: [
        pro,
        autoHeightTextareaPlugin,
        handleHttpErrorsPlugin,
        clearServerErrorsPlugin,
      ],
      locales: { en, nl },
      locale: 'en',
      rules: {
        naturallyBefore,
        naturallyAfter,
        postcode: postalCodeValidation,
        phoneNumber: phoneNumberValidation,
        taxIdFormat: taxIdFormatValidation,
      },
      messages: {
        en: {
          validation: {
            postcode: getPostcodeValidationMessage,
            phoneNumber: getPhoneNumberValidationMessage,
            naturallyBefore: getNaturalOrderValidationMessage.naturallyBefore,
            naturallyAfter: getNaturalOrderValidationMessage.naturallyAfter,
            taxIdFormat: getTaxIdFormatValidationMessage,
          },
        },
        nl: {
          validation: {
            postcode: getPostcodeValidationMessage,
            phoneNumber: getPhoneNumberValidationMessage,
            naturallyBefore: getNaturalOrderValidationMessage.naturallyBefore,
            naturallyAfter: getNaturalOrderValidationMessage.naturallyAfter,
            taxIdFormat: getTaxIdFormatValidationMessage,
          },
        },
      },
      inputs: {
        amount: createInput(
          FormulateAmount,
          {
            props: ['currency'],
            features: [
              function (node) {
                node.on('created', () => {
                  if (!node.context) return;
                  node.context.currencySymbol = getCurrencySymbol(
                    node.props.currency || 'eur'
                  );
                });
              },
            ],
          },
          {
            prefix: {
              $el: 'div',
              attrs: {
                class: 'formkit-currency-symbol',
              },
              children: '$currencySymbol',
            },
          }
        ),
        findandselect: createInput(FindAndSelect, {
          props: [
            'searchKeys',
            'showCount',
            'hideNoResult',
            'placeholder',
            'itemClass',
            'showOnFocus',
            'itemClass',
            'options',
            'addOther',
            'itemComponent',
            'onClear',
            'onSet',
            'onSearchInput',
            'noResultMessage',
            'filteredOptions',
            'limit',
            'loading',
          ],
        }),
        quantity: createInput(Quantity),
        phonenumber: createInput(PhoneNumberInput),
        inlineselect: createInput(FormulateInlineSelect, {
          props: ['options'],
        }),
      },
    })
  );
}

const clearServerErrorsPlugin = (node: FormKitNode) => {
  if (node.props.type === 'form' || node.props.type === 'group') {
    node.on('submit-raw', () => {
      node.setErrors([], {});
    });
  }
};

const handleHttpErrorsPlugin = (node: FormKitNode) => {
  if (node.props.type !== 'form') return;
  node.props.actions = node.props.actions ?? false;
  node.props.incompleteMessage = node.props.incompleteMessage ?? false;

  if (node.context?.handlers) {
    node.context.handlers.httpError = (error) => {
      const formKitErrors = errorHandler(error);
      if (!formKitErrors) return;

      node.setErrors(
        formKitErrors.formErrors,
        formKitErrors.inputErrors as Record<string, string>
      );
    };
  }
};

// the formkit auto height textarea plugin sucks really bad wtf
function autoHeightTextareaPlugin(node: FormKitNode) {
  if (node.props.type !== 'textarea') return;
  node.addProps(['autoHeight']);
  node.on('mounted', () => {
    const autoHeight = undefine(node.props.autoHeight);

    if (!autoHeight || !node.context) return;

    whenAvailable(
      node.context.id,
      () => {
        const inputElement = node.props.__root?.getElementById(
          node.context?.id ? node.context.id : ''
        );
        if (!(inputElement instanceof HTMLTextAreaElement)) return;

        const resize = () => {
          inputElement.style.height = 'auto';
          inputElement.style.height = inputElement.scrollHeight + 'px';
        };

        resize();
        node.on('input', resize);
      },
      node.props.__root
    );
  });
}
